import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, RefreshCw, Mail, Shield, Lock, Unlock, AlertTriangle, CheckCircle, XCircle, Activity, Edit, Eye, Key, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { userAccountService } from '../../services/userAccountService';
import { useAuth } from '../../hooks/useAuth';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { LoadingState } from '../../components/ui/LoadingStates';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'job_seeker', label: 'Job Seeker' },
  { value: 'hiring_manager', label: 'Hiring Manager' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'admin', label: 'Admin' }
];

const ACCOUNT_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'locked', label: 'Locked' }
];

const EMAIL_VERIFIED_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'unverified', label: 'Unverified' }
];

export default function AdminUserAccountManagement() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [activityTimeline, setActivityTimeline] = useState([]);
  const [adminActivityLog, setAdminActivityLog] = useState([]);
  const [userTickets, setUserTickets] = useState([]);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [actionReason, setActionReason] = useState('');
  const [actionValue, setActionValue] = useState('');
  const [processingAction, setProcessingAction] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [accountStatusFilter, setAccountStatusFilter] = useState('all');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 20;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [roleFilter, accountStatusFilter, emailVerifiedFilter, searchQuery]);

  const loadData = async () => {
    try {
      const stats = await userAccountService?.getUserStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const filters = {
        search: searchQuery,
        role: roleFilter,
        accountStatus: accountStatusFilter,
        emailVerified: emailVerifiedFilter
      };

      const data = await userAccountService?.searchUsers(filters);
      setUsers(data);
    } catch (err) {
      setError(err?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserDetails = async (user) => {
    try {
      setSelectedUser(user);
      setShowUserDetailsModal(true);
      setLoading(true);

      const [details, timeline, activityLog, tickets] = await Promise.all([
        userAccountService?.getUserDetails(user?.id),
        userAccountService?.getUserActivityTimeline(user?.id, 30),
        userAccountService?.getUserAdminActivityLog(user?.id, 20),
        userAccountService?.getUserTickets(user?.id)
      ]);

      setUserDetails(details);
      setActivityTimeline(timeline);
      setAdminActivityLog(activityLog);
      setUserTickets(tickets);
    } catch (err) {
      setError(err?.message || 'Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleUserAction = (user, action) => {
    setSelectedUser(user);
    setActionType(action);
    setActionReason('');
    setActionValue('');
    setShowActionModal(true);
  };

  const executeUserAction = async () => {
    if (!actionReason?.trim()) {
      setError('Please provide a reason for this action');
      return;
    }

    try {
      setProcessingAction(true);
      setError('');

      switch (actionType) {
        case 'unlock_account':
          await userAccountService?.unlockAccount(selectedUser?.id, actionReason);
          setSuccess('Account unlocked successfully');
          break;
        case 'lock_account':
          await userAccountService?.lockAccount(selectedUser?.id, actionReason);
          setSuccess('Account locked successfully');
          break;
        case 'verify_email':
          await userAccountService?.verifyUserEmail(selectedUser?.id, actionReason);
          setSuccess('Email verified successfully');
          break;
        case 'reset_password':
          await userAccountService?.adminResetPassword(selectedUser?.id, actionReason);
          setSuccess('Password reset email sent successfully');
          break;
        case 'modify_data':
          if (!actionValue?.trim()) {
            setError('Please provide a new value');
            return;
          }
          await userAccountService?.modifyUserData(
            selectedUser?.id,
            'full_name',
            actionValue,
            actionReason
          );
          setSuccess('User data modified successfully');
          break;
        case 'override_deletion':
          await userAccountService?.overrideAccountDeletion(selectedUser?.id, actionReason);
          setSuccess('Account deletion prevented successfully');
          break;
        default:
          setError('Unknown action type');
      }

      setShowActionModal(false);
      await loadUsers();
      if (showUserDetailsModal) {
        await handleViewUserDetails(selectedUser);
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Action failed');
    } finally {
      setProcessingAction(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      job_seeker: 'bg-blue-100 text-blue-800 border-blue-300',
      hiring_manager: 'bg-green-100 text-green-800 border-green-300',
      company_admin: 'bg-purple-100 text-purple-800 border-purple-300',
      admin: 'bg-red-100 text-red-800 border-red-300'
    };
    return colors?.[role] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getStatusBadgeColor = (accountLocked) => {
    return accountLocked
      ? 'bg-red-100 text-red-800 border-red-300' :'bg-green-100 text-green-800 border-green-300';
  };

  const getEventTypeIcon = (eventType) => {
    const icons = {
      profile_update: Edit,
      job_application: FileText,
      support_ticket: AlertTriangle,
      admin_action: Shield,
      password_reset: Key
    };
    return icons?.[eventType] || Activity;
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users?.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(users?.length / usersPerPage);

  if (loading && !users?.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingState />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Account Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Comprehensive user support and account administration tools
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={loadData}
                className="flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
        </div>
      </div>
      {/* Success/Error Messages */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-7xl mx-auto px-6 pt-4"
          >
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span>{success}</span>
            </div>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="max-w-7xl mx-auto px-6 pt-4"
          >
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center space-x-2">
              <XCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Statistics Cards */}
      {statistics && (
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {statistics?.totalUsers}
                  </p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    {statistics?.activeUsers}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Locked Accounts</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {statistics?.lockedUsers}
                  </p>
                </div>
                <Lock className="w-8 h-8 text-red-500" />
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Unverified Emails</p>
                  <p className="text-2xl font-bold text-orange-600 mt-1">
                    {statistics?.unverifiedEmails}
                  </p>
                </div>
                <Mail className="w-8 h-8 text-orange-500" />
              </div>
            </Card>
          </div>
        </div>
      )}
      {/* Filters */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <Card className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Users
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {ROLE_OPTIONS?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Status
              </label>
              <select
                value={accountStatusFilter}
                onChange={(e) => setAccountStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {ACCOUNT_STATUS_OPTIONS?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Status
              </label>
              <select
                value={emailVerifiedFilter}
                onChange={(e) => setEmailVerifiedFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {EMAIL_VERIFIED_OPTIONS?.map(option => (
                  <option key={option?.value} value={option?.value}>
                    {option?.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      </div>
      {/* Users Table */}
      <div className="max-w-7xl mx-auto px-6 pb-6">
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentUsers?.map(user => (
                  <tr key={user?.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user?.avatarUrl ? (
                            <img
                              className="h-10 w-10 rounded-full"
                              src={user?.avatarUrl}
                              alt={user?.fullName || 'User'}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-blue-600 font-medium text-sm">
                                {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user?.fullName || 'Unnamed User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getRoleBadgeColor(user?.role)}`}>
                        {user?.role?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadgeColor(user?.accountLocked)}`}>
                          {user?.accountLocked ? 'Locked' : 'Active'}
                        </span>
                        {user?.emailVerified ? (
                          <span className="text-xs text-green-600 flex items-center">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verified
                          </span>
                        ) : (
                          <span className="text-xs text-orange-600 flex items-center">
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Unverified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user?.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user?.lastActiveAt
                        ? new Date(user?.lastActiveAt)?.toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewUserDetails(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {user?.accountLocked ? (
                          <button
                            onClick={() => handleUserAction(user, 'unlock_account')}
                            className="text-green-600 hover:text-green-900"
                            title="Unlock Account"
                          >
                            <Unlock className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUserAction(user, 'lock_account')}
                            className="text-red-600 hover:text-red-900"
                            title="Lock Account"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleUserAction(user, 'reset_password')}
                          className="text-purple-600 hover:text-purple-900"
                          title="Reset Password"
                        >
                          <Key className="w-4 h-4" />
                        </button>
                        {!user?.emailVerified && (
                          <button
                            onClick={() => handleUserAction(user, 'verify_email')}
                            className="text-orange-600 hover:text-orange-900"
                            title="Verify Email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstUser + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastUser, users?.length)}
                    </span>{' '}
                    of <span className="font-medium">{users?.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    {[...Array(totalPages)]?.map((_, i) => (
                      <button
                        key={i + 1}
                        onClick={() => setCurrentPage(i + 1)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === i + 1
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' :'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
      {/* User Details Modal */}
      <AnimatePresence>
        {showUserDetailsModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowUserDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e?.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">User Account Details</h2>
                  <button
                    onClick={() => setShowUserDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {userDetails && (
                  <div className="space-y-6">
                    {/* User Profile Section */}
                    <div className="border-b border-gray-200 pb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Profile Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Full Name</p>
                          <p className="text-sm font-medium text-gray-900">{userDetails?.user?.fullName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Email</p>
                          <p className="text-sm font-medium text-gray-900">{userDetails?.user?.email}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Role</p>
                          <p className="text-sm font-medium text-gray-900">{userDetails?.user?.role?.replace('_', ' ')}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Account Status</p>
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full border ${getStatusBadgeColor(userDetails?.user?.accountLocked)}`}>
                            {userDetails?.user?.accountLocked ? 'Locked' : 'Active'}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Email Verified</p>
                          <p className="text-sm font-medium text-gray-900">
                            {userDetails?.user?.emailVerified ? 'Yes' : 'No'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Last Active</p>
                          <p className="text-sm font-medium text-gray-900">
                            {userDetails?.user?.lastActiveAt
                              ? new Date(userDetails?.user?.lastActiveAt)?.toLocaleString()
                              : 'Never'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Activity Summary */}
                    <div className="border-b border-gray-200 pb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h3>
                      <div className="grid grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-blue-600">{userDetails?.activity?.total_applications || 0}</p>
                          <p className="text-sm text-gray-600">Applications</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-green-600">{userDetails?.activity?.total_tickets || 0}</p>
                          <p className="text-sm text-gray-600">Tickets</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-orange-600">{userDetails?.activity?.open_tickets || 0}</p>
                          <p className="text-sm text-gray-600">Open Tickets</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-purple-600">{userDetails?.activity?.admin_actions || 0}</p>
                          <p className="text-sm text-gray-600">Admin Actions</p>
                        </div>
                      </div>
                    </div>

                    {/* Activity Timeline */}
                    <div className="border-b border-gray-200 pb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h3>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {activityTimeline?.slice(0, 10)?.map((event, index) => {
                          const EventIcon = getEventTypeIcon(event?.eventType);
                          return (
                            <div key={index} className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <EventIcon className="w-5 h-5 text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">{event?.eventDescription}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(event?.eventTime)?.toLocaleString()}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUserDetailsModal(false);
                          handleUserAction(selectedUser, userDetails?.user?.accountLocked ? 'unlock_account' : 'lock_account');
                        }}
                        className="flex items-center space-x-2"
                      >
                        {userDetails?.user?.accountLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                        <span>{userDetails?.user?.accountLocked ? 'Unlock Account' : 'Lock Account'}</span>
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUserDetailsModal(false);
                          handleUserAction(selectedUser, 'reset_password');
                        }}
                        className="flex items-center space-x-2"
                      >
                        <Key className="w-4 h-4" />
                        <span>Reset Password</span>
                      </Button>
                      {!userDetails?.user?.emailVerified && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowUserDetailsModal(false);
                            handleUserAction(selectedUser, 'verify_email');
                          }}
                          className="flex items-center space-x-2"
                        >
                          <Mail className="w-4 h-4" />
                          <span>Verify Email</span>
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowUserDetailsModal(false);
                          handleUserAction(selectedUser, 'modify_data');
                        }}
                        className="flex items-center space-x-2"
                      >
                        <Edit className="w-4 h-4" />
                        <span>Modify Data</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Action Modal */}
      <AnimatePresence>
        {showActionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowActionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
              onClick={(e) => e?.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {actionType?.replace('_', ' ')?.toUpperCase()}
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                User: <span className="font-medium">{selectedUser?.fullName || selectedUser?.email}</span>
              </p>

              {actionType === 'modify_data' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Value
                  </label>
                  <Input
                    type="text"
                    value={actionValue}
                    onChange={(e) => setActionValue(e?.target?.value)}
                    placeholder="Enter new value..."
                  />
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason (Required)
                </label>
                <textarea
                  value={actionReason}
                  onChange={(e) => setActionReason(e?.target?.value)}
                  placeholder="Provide a reason for this action..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowActionModal(false)}
                  disabled={processingAction}
                >
                  Cancel
                </Button>
                <Button
                  onClick={executeUserAction}
                  disabled={processingAction || !actionReason?.trim()}
                >
                  {processingAction ? 'Processing...' : 'Confirm'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}